import React, { useState } from 'react';
import { Sparkles, Shield, Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from './Toast';
import type { CutRange } from '@vidly/types';

interface SettingsStepProps {
  maxDurationSeconds: number;
  actualVideoDurationSeconds?: number;
  onSubmit: (settings: {
    instructions: string;
    targetDurationSeconds: number;
    mandatoryRanges: CutRange[];
  }) => void;
  isLoading: boolean;
}

export const SettingsStep: React.FC<SettingsStepProps> = ({
  maxDurationSeconds,
  actualVideoDurationSeconds = 300,
  onSubmit,
  isLoading
}) => {
  const videoDuration = Math.min(actualVideoDurationSeconds, maxDurationSeconds);
  const defaultTarget = Math.max(5, Math.min(Math.round(videoDuration * 0.5), videoDuration - 2));

  const [instructions, setInstructions] = useState('');
  const [targetDurationSeconds, setTargetDurationSeconds] = useState(defaultTarget);
  const [mandatoryRanges, setMandatoryRanges] = useState<CutRange[]>([]);

  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [newReason, setNewReason] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const parseTimeToSeconds = (val: string): number => {
    if (val.includes(':')) {
      const [m, s] = val.split(':').map(Number);
      return (m || 0) * 60 + (s || 0);
    }
    return parseFloat(val) || 0;
  };

  const formatSeconds = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddMandatoryRange = () => {
    setInputError(null);
    const startSec = parseTimeToSeconds(newStart);
    const endSec = parseTimeToSeconds(newEnd);

    if (isNaN(startSec) || isNaN(endSec) || startSec < 0 || endSec <= startSec) {
      setInputError('Introduce un intervalo válido (el fin debe ser mayor que el inicio).');
      return;
    }

    if (endSec > videoDuration) {
      setInputError(`El fin (${formatSeconds(endSec)}) no puede superar la duración total (${formatSeconds(videoDuration)}).`);
      return;
    }

    if (endSec - startSec < 1) {
      setInputError('El intervalo debe durar al menos 1 segundo.');
      return;
    }

    setMandatoryRanges([
      ...mandatoryRanges,
      {
        start: startSec,
        end: endSec,
        reason: newReason.trim() || 'Intervalo protegido por el usuario',
        isMandatory: true
      }
    ]);

    setNewStart('');
    setNewEnd('');
    setNewReason('');
  };

  const handleRemoveMandatory = (index: number) => {
    setMandatoryRanges(mandatoryRanges.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalInstructions = instructions.trim() || 'Resumen equilibrado con gancho inicial, ideas principales y conclusión clara.';
    onSubmit({
      instructions: finalInstructions,
      targetDurationSeconds,
      mandatoryRanges
    });
  };

  const applyPreset = (presetText: string) => {
    setInstructions(presetText);
    toast.info('Plantilla aplicada', 'Instrucciones editoriales configuradas.');
  };

  const minSlider = Math.min(5, Math.floor(videoDuration * 0.2));
  const maxSlider = videoDuration;
  const stepSlider = videoDuration <= 60 ? 1 : (videoDuration <= 300 ? 5 : 15);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-left max-w-2xl mx-auto">
      
      {/* 1. Natural Language Instructions */}
      <div className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/30 space-y-2.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-zinc-200 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-zinc-400" />
            <span>Instrucciones editoriales para la IA</span>
          </label>
          <span className="text-[11px] text-zinc-500">{instructions.length}/500</span>
        </div>
        
        <p className="text-xs text-zinc-400">
          Describe qué contenido conservar. La IA analiza las oraciones y sincroniza los cortes en pausas naturales para no cortar nunca a media frase.
        </p>

        {/* Quick Editorial Presets */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[11px] text-zinc-500 mr-1">Plantillas:</span>
          <button
            type="button"
            onClick={() => applyPreset('Resumen equilibrado con gancho inicial, puntos centrales y conclusión clara.')}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-white transition-colors"
          >
            🎯 Equilibrado
          </button>
          <button
            type="button"
            onClick={() => applyPreset('Máximo ritmo: elimina rodeos, silencios y charlas secundarias, conservando solo los momentos de más impacto.')}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-white transition-colors"
          >
            ⚡ Dinámico
          </button>
          <button
            type="button"
            onClick={() => applyPreset('Extrae los conceptos y conclusiones clave omitiendo anécdotas o introducciones largas.')}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-white transition-colors"
          >
            💡 Puntos clave
          </button>
          <button
            type="button"
            onClick={() => applyPreset('Conserva el hilo completo del vídeo eliminando pausas largas, titubeos y silencios.')}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-white transition-colors"
          >
            ✂️ Sin silencios
          </button>
        </div>

        <textarea
          rows={3}
          maxLength={500}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value.slice(0, 500))}
          placeholder="O escribe tus propias instrucciones (ej: Conserva la demo y el análisis final)..."
          className="w-full bg-zinc-950/80 border border-zinc-800 focus:border-zinc-600 rounded-lg p-3 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none resize-none transition-colors"
        />
      </div>

      {/* 2. Target Duration Slider */}
      <div className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/30 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-zinc-200 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-zinc-400" />
            <span>Duración objetivo del resumen</span>
          </label>
          <span className="text-xs font-semibold text-white px-2 py-0.5 rounded bg-zinc-800 border border-zinc-700">
            {formatSeconds(targetDurationSeconds)}
          </span>
        </div>

        <p className="text-xs text-zinc-500">
          Vídeo original: {formatSeconds(videoDuration)}. La IA seleccionará los fragmentos hasta sumar este tiempo aproximado.
        </p>

        <div className="space-y-1.5 pt-1">
          <input
            type="range"
            min={minSlider}
            max={maxSlider}
            step={stepSlider}
            value={targetDurationSeconds}
            onChange={(e) => setTargetDurationSeconds(Number(e.target.value))}
            className="w-full h-1.5 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-white"
          />

          <div className="flex justify-between text-[11px] text-zinc-500">
            <span>{formatSeconds(minSlider)}</span>
            <span>{formatSeconds(maxSlider)}</span>
          </div>
        </div>
      </div>

      {/* 3. Mandatory Protected Ranges */}
      <div className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/30 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-zinc-200 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-zinc-400" />
            <span>Intervalos obligatorios (opcional)</span>
          </label>
          <span className="text-[11px] text-zinc-500">Inmutables</span>
        </div>

        <p className="text-xs text-zinc-500">
          Fragmentos que la IA jamás recortará ni eliminará del metraje final.
        </p>

        {/* Existing list */}
        {mandatoryRanges.length > 0 && (
          <div className="space-y-1.5">
            {mandatoryRanges.map((range, idx) => (
              <div
                key={idx}
                className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-zinc-950/60 border border-zinc-800 text-xs"
              >
                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                  <span className="font-medium text-zinc-200 shrink-0">
                    {formatSeconds(range.start)} → {formatSeconds(range.end)}
                  </span>
                  <span className="text-zinc-500 text-[11px] truncate">({range.reason})</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveMandatory(idx)}
                  className="text-zinc-500 hover:text-red-400 transition-colors p-1 shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add input row */}
        <div className="pt-2 border-t border-zinc-800/80 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs">
            <input
              type="text"
              placeholder="Inicio (00:05)"
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="bg-zinc-950/80 border border-zinc-800 rounded-lg p-2 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            />
            <input
              type="text"
              placeholder="Fin (00:25)"
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              className="bg-zinc-950/80 border border-zinc-800 rounded-lg p-2 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            />
            <input
              type="text"
              placeholder="Motivo (ej: Conclusión)"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              className="sm:col-span-2 bg-zinc-950/80 border border-zinc-800 rounded-lg p-2 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            />
          </div>

          {inputError && (
            <p className="text-xs text-red-400">{inputError}</p>
          )}

          <button
            type="button"
            onClick={handleAddMandatoryRange}
            className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Añadir intervalo protegido</span>
          </button>
        </div>
      </div>

      {/* Submit button */}
      <div>
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-2.5 text-xs font-medium"
        >
          {isLoading ? 'Planificando con IA...' : 'Generar plan de cortes →'}
        </button>
      </div>

    </form>
  );
};
